import type { ReportType, InterventionLevel } from "../../../api/client/types/catalog.types";

/**
 * Report type names - these are stable identifiers that don't change even if IDs do.
 * Using lowercase for case-insensitive comparison.
 */
export const REPORT_TYPE_NAMES = {
  MAINTENANCE: "mantenimiento",
  POOL_REGULATION: "incumplimiento reglamento de piscina",
  ADVERSE_EVENTS: "eventos adversos",
  FIRST_AID: "puesto de p.a / botiquín",
} as const;

/**
 * Adverse event type names - these are stable identifiers that don't change even if IDs do.
 * Used for business logic related to adverse event types.
 */
export const ADVERSE_EVENT_TYPE_NAMES = {
  METEOROLOGICAL_CONDITIONS: "Condiciones Meteorológicas",
  ACCIDENT: "Accidente",
  INCIDENTS: "Incidentes",
  VERBAL_PHYSICAL_AGGRESSION: "Agresión Verbal/Física",
} as const;

/**
 * Maintenance type names - these are stable identifiers that don't change even if IDs do.
 * Used for business logic related to maintenance types.
 */
export const MAINTENANCE_TYPE_NAMES = {
  EMERGENCY_ELEMENTS: "Elementos de Emergencia",
} as const;

/**
 * Intervention level names that do NOT require security_officer_name.
 * These intervention levels are treated similarly to "Puesto de P.A / Botiquín".
 */
export const INTERVENTION_LEVEL_NAMES_NO_SECURITY_OFFICER = {
  LLAMADO_ATENCION_1: "Llamado de Atención 1",
  LLAMADO_ATENCION_2: "Llamado de Atención 2",
} as const;

/**
 * Prefix used to distinguish adverse event type IDs from regular report type IDs in the UI.
 * Format: "adverse_{adverseEventTypeId}"
 * Example: "adverse_3" represents adverse event type with ID 3
 */
export const ADVERSE_EVENT_ID_PREFIX = "adverse_" as const;

/**
 * Prefix used to distinguish maintenance type IDs from regular report type IDs in the UI.
 * Format: "maintenance_{maintenanceTypeId}"
 * Example: "maintenance_1" represents maintenance type with ID 1
 */
export const MAINTENANCE_TYPE_ID_PREFIX = "maintenance_" as const;

/**
 * Normalize string for stable comparison (lowercase and trim).
 * This ensures case-insensitive and whitespace-tolerant string matching.
 *
 * @param str - The string to normalize
 * @returns Normalized string (lowercase, trimmed)
 */
export const normalizeString = (str: string): string => {
  return str.toLowerCase().trim();
};

/**
 * Find a report type by its normalized name from a list of report types.
 *
 * @param reportTypes - Array of report types to search in
 * @param name - The normalized name to match (should be from REPORT_TYPE_NAMES)
 * @returns The matching report type, or undefined if not found
 */
export const findReportTypeByName = (
  reportTypes: ReportType[] | undefined,
  name: string
): ReportType | undefined => {
  if (!reportTypes) return undefined;
  return reportTypes.find((rt) => normalizeString(rt.name) === name);
};

/**
 * Generic helper to check if a report type ID corresponds to a specific report type name.
 *
 * @param reportTypeId - The report type ID to check (as string or number)
 * @param reportTypeName - The normalized name of the report type to match
 * @param reportTypes - Array of report types to search in
 * @returns true if the ID corresponds to the specified report type, false otherwise
 */
const isReportTypeByName = (
  reportTypeId: string | number,
  reportTypeName: string,
  reportTypes: ReportType[] | undefined
): boolean => {
  const reportType = findReportTypeByName(reportTypes, reportTypeName);
  if (!reportType) return false;

  const idToCompare =
    typeof reportTypeId === "string"
      ? parseInt(reportTypeId, 10)
      : reportTypeId;
  return idToCompare === reportType.id;
};

/**
 * Check if a report type ID corresponds to the maintenance report type.
 *
 * @param reportTypeId - The report type ID to check (as string or number)
 * @param reportTypes - Array of report types to search in
 * @returns true if the ID corresponds to maintenance type, false otherwise
 */
export const isMaintenanceReportType = (
  reportTypeId: string | number,
  reportTypes: ReportType[] | undefined
): boolean => {
  return isReportTypeByName(
    reportTypeId,
    REPORT_TYPE_NAMES.MAINTENANCE,
    reportTypes
  );
};

/**
 * Check if a report type ID corresponds to the first aid station report type.
 *
 * @param reportTypeId - The report type ID to check (as string or number)
 * @param reportTypes - Array of report types to search in
 * @returns true if the ID corresponds to first aid station type, false otherwise
 */
export const isFirstAidStationReportType = (
  reportTypeId: string | number,
  reportTypes: ReportType[] | undefined
): boolean => {
  return isReportTypeByName(
    reportTypeId,
    REPORT_TYPE_NAMES.FIRST_AID,
    reportTypes
  );
};

/**
 * Check if a report type ID corresponds to the pool regulation report type.
 *
 * @param reportTypeId - The report type ID to check (as string or number)
 * @param reportTypes - Array of report types to search in
 * @returns true if the ID corresponds to pool regulation type, false otherwise
 */
export const isPoolRegulationReportType = (
  reportTypeId: string | number,
  reportTypes: ReportType[] | undefined
): boolean => {
  return isReportTypeByName(
    reportTypeId,
    REPORT_TYPE_NAMES.POOL_REGULATION,
    reportTypes
  );
};

/**
 * Check if a report type ID corresponds to the adverse events report type.
 *
 * @param reportTypeId - The report type ID to check (as string or number)
 * @param reportTypes - Array of report types to search in
 * @returns true if the ID corresponds to adverse events type, false otherwise
 */
export const isAdverseEventReportType = (
  reportTypeId: string | number,
  reportTypes: ReportType[] | undefined
): boolean => {
  return isReportTypeByName(
    reportTypeId,
    REPORT_TYPE_NAMES.ADVERSE_EVENTS,
    reportTypes
  );
};

/**
 * Check if a value is an adverse event type ID (has the adverse event prefix).
 * This is used in the UI where adverse event types are displayed as report type options.
 *
 * @param value - The value to check (typically from form data)
 * @returns true if the value represents an adverse event type ID
 * @example
 * isAdverseEventTypeValue("adverse_3") // true
 * isAdverseEventTypeValue("5") // false
 */
export const isAdverseEventTypeValue = (value: string | number): boolean => {
  return value.toString().startsWith(ADVERSE_EVENT_ID_PREFIX);
};

/**
 * Extract the adverse event type ID from a prefixed value.
 *
 * @param prefixedValue - The prefixed value (e.g., "adverse_3")
 * @returns The extracted adverse event type ID as a string (e.g., "3")
 * @throws Error if the value doesn't have the adverse event prefix
 * @example
 * extractAdverseEventTypeId("adverse_3") // "3"
 */
export const extractAdverseEventTypeId = (
  prefixedValue: string | number
): string => {
  const stringValue = prefixedValue.toString();
  
  if (!isAdverseEventTypeValue(stringValue)) {
    throw new Error(
      `Invalid adverse event type value: ${stringValue}. Expected format: ${ADVERSE_EVENT_ID_PREFIX}{id}`
    );
  }
  
  return stringValue.replace(ADVERSE_EVENT_ID_PREFIX, "");
};

/**
 * Create a prefixed adverse event type value for use in the UI.
 *
 * @param adverseEventTypeId - The adverse event type ID
 * @returns The prefixed value (e.g., "adverse_3")
 * @example
 * createAdverseEventTypeValue(3) // "adverse_3"
 */
export const createAdverseEventTypeValue = (
  adverseEventTypeId: string | number
): string => {
  return `${ADVERSE_EVENT_ID_PREFIX}${adverseEventTypeId}`;
};

/**
 * Check if a value is a maintenance type ID (has the maintenance type prefix).
 * This is used in the UI where maintenance types are displayed as report type options.
 *
 * @param value - The value to check (typically from form data)
 * @returns true if the value represents a maintenance type ID
 * @example
 * isMaintenanceTypeValue("maintenance_1") // true
 * isMaintenanceTypeValue("5") // false
 */
export const isMaintenanceTypeValue = (value: string | number): boolean => {
  return value.toString().startsWith(MAINTENANCE_TYPE_ID_PREFIX);
};

/**
 * Extract the maintenance type ID from a prefixed value.
 *
 * @param prefixedValue - The prefixed value (e.g., "maintenance_1")
 * @returns The extracted maintenance type ID as a string (e.g., "1")
 * @throws Error if the value doesn't have the maintenance type prefix
 * @example
 * extractMaintenanceTypeId("maintenance_1") // "1"
 */
export const extractMaintenanceTypeId = (
  prefixedValue: string | number
): string => {
  const stringValue = prefixedValue.toString();
  
  if (!isMaintenanceTypeValue(stringValue)) {
    throw new Error(
      `Invalid maintenance type value: ${stringValue}. Expected format: ${MAINTENANCE_TYPE_ID_PREFIX}{id}`
    );
  }
  
  return stringValue.replace(MAINTENANCE_TYPE_ID_PREFIX, "");
};

/**
 * Create a prefixed maintenance type value for use in the UI.
 *
 * @param maintenanceTypeId - The maintenance type ID
 * @returns The prefixed value (e.g., "maintenance_1")
 * @example
 * createMaintenanceTypeValue(1) // "maintenance_1"
 */
export const createMaintenanceTypeValue = (
  maintenanceTypeId: string | number
): string => {
  return `${MAINTENANCE_TYPE_ID_PREFIX}${maintenanceTypeId}`;
};

/**
 * Check if the selected intervention level is "Llamado de Atención 1" or "Llamado de Atención 2".
 * These intervention levels do NOT require security_officer_name (similar to First Aid Station).
 *
 * @param interventionLevelId - The intervention level ID to check (as string or undefined)
 * @param interventionLevels - Array of intervention levels to search in
 * @returns true if the intervention level doesn't require security officer name, false otherwise
 */
export const isLlamadoDeAtencionInterventionLevel = (
  interventionLevelId: string | undefined,
  interventionLevels?: InterventionLevel[]
): boolean => {
  if (!interventionLevelId || !interventionLevels) {
    return false;
  }
  
  // Find the intervention level by ID
  const selectedInterventionLevel = interventionLevels.find(
    (level) => level.id.toString() === interventionLevelId
  );
  
  if (!selectedInterventionLevel) {
    return false;
  }
  
  // Check if the intervention level is one that doesn't require security officer
  const interventionLevelName = selectedInterventionLevel.name;
  return (
    interventionLevelName === INTERVENTION_LEVEL_NAMES_NO_SECURITY_OFFICER.LLAMADO_ATENCION_1 ||
    interventionLevelName === INTERVENTION_LEVEL_NAMES_NO_SECURITY_OFFICER.LLAMADO_ATENCION_2
  );
};
